import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMsal } from '@azure/msal-react';
import {
  BenefitsPerTierError,
  EMPTY_ELIGIBILITY,
  type Benefit,
  type BenefitsPerTierService,
} from '../services/benefitsPerTierService';
import { ApiService } from '../services/apiService';
import { HttpBenefitsPerTierService } from '../services/httpBenefitsPerTierService';
import {
  getFriendlyApiErrorMessage,
  logApiError,
} from '../services/apiErrorMessages';
import type { TierKey } from '../constants/tiers';

interface AddInput {
  name: string;
  eligibility?: Record<TierKey, boolean>;
}

interface UpdateInput {
  name: string;
  eligibility: Record<TierKey, boolean>;
}

interface BenefitsPerTierContextValue {
  benefits: Benefit[];
  loading: boolean;
  loadError: string | null;
  hasPending: boolean;
  addBenefit: (input: AddInput) => Promise<void>;
  updateBenefit: (id: string, input: UpdateInput) => Promise<void>;
  removeBenefit: (id: string) => Promise<void>;
  cancelPendingForBenefit: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const BenefitsPerTierContext = createContext<BenefitsPerTierContextValue | undefined>(
  undefined,
);

interface ProviderProps {
  children: React.ReactNode;
  service?: BenefitsPerTierService;
}

const normalizeName = (s: string) => s.trim().toLowerCase();

export const BenefitsPerTierProvider: React.FC<ProviderProps> = ({
  children,
  service: serviceOverride,
}) => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  const httpService = useMemo<BenefitsPerTierService | null>(() => {
    if (serviceOverride || !account) return null;
    const api = new ApiService(instance, account);
    return new HttpBenefitsPerTierService(api);
  }, [serviceOverride, instance, account]);

  const service = serviceOverride ?? httpService;

  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!service) {
      setBenefits([]);
      setLoading(false);
      setLoadError('No authenticated account found.');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await service.list();
      setBenefits(data);
    } catch (err) {
      logApiError('BenefitsPerTierContext.refresh', err);
      setLoadError(getFriendlyApiErrorMessage(err, 'Failed to load benefits.'));
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const requireService = useCallback((): BenefitsPerTierService => {
    if (!service) {
      throw new BenefitsPerTierError(
        'NOT_FOUND',
        'No authenticated account; cannot mutate benefits.',
      );
    }
    return service;
  }, [service]);

  const addBenefit = useCallback<BenefitsPerTierContextValue['addBenefit']>(
    async ({ name, eligibility }) => {
      const trimmed = name.trim();
      if (!trimmed) {
        throw new BenefitsPerTierError('EMPTY_NAME', 'Benefit name is required.');
      }
      if (benefits.some((b) => normalizeName(b.name) === normalizeName(trimmed))) {
        throw new BenefitsPerTierError(
          'DUPLICATE_NAME',
          `A benefit named "${trimmed}" already exists.`,
        );
      }
      const svc = requireService();
      await svc.upsertBenefit(trimmed, eligibility ?? { ...EMPTY_ELIGIBILITY });
      await refresh();
    },
    [benefits, refresh, requireService],
  );

  const updateBenefit = useCallback<BenefitsPerTierContextValue['updateBenefit']>(
    async (id, { name, eligibility }) => {
      const trimmed = name.trim();
      if (!trimmed) {
        throw new BenefitsPerTierError('EMPTY_NAME', 'Benefit name is required.');
      }
      const target = benefits.find((b) => b.id === id);
      if (!target) {
        throw new BenefitsPerTierError('NOT_FOUND', 'Benefit not found.');
      }
      const targetKey = normalizeName(trimmed);
      if (
        benefits.some(
          (b) => b.id !== id && normalizeName(b.name) === targetKey,
        )
      ) {
        throw new BenefitsPerTierError(
          'DUPLICATE_NAME',
          `A benefit named "${trimmed}" already exists.`,
        );
      }

      const svc = requireService();
      const renamed = normalizeName(target.name) !== targetKey;
      const isPendingCreate = target.pending?.operation === 'create';

      if (renamed) {
        if (isPendingCreate) {

          await svc.cancelPendingForBenefit(target.name);
          await svc.upsertBenefit(trimmed, eligibility);
        } else {
          await svc.renameBenefit(target.name, trimmed);

          const inheritedEligibility = target.eligibility;
          const eligibilityChanged = (Object.keys(eligibility) as TierKey[]).some(
            (k) => eligibility[k] !== inheritedEligibility[k],
          );
          if (eligibilityChanged) {
            await svc.upsertBenefit(trimmed, eligibility);
          }
        }
      } else {
        await svc.upsertBenefit(trimmed, eligibility);
      }
      await refresh();
    },
    [benefits, refresh, requireService],
  );

  const removeBenefit = useCallback<BenefitsPerTierContextValue['removeBenefit']>(
    async (id) => {
      const target = benefits.find((b) => b.id === id);
      if (!target) {
        throw new BenefitsPerTierError('NOT_FOUND', 'Benefit not found.');
      }
      const svc = requireService();
      if (target.pending?.operation === 'create') {
        await svc.cancelPendingForBenefit(target.name);
      } else {
        await svc.deleteBenefit(target.name);
      }
      await refresh();
    },
    [benefits, refresh, requireService],
  );

  const cancelPendingForBenefit = useCallback<BenefitsPerTierContextValue['cancelPendingForBenefit']>(
    async (id) => {
      const target = benefits.find((b) => b.id === id);
      if (!target) {
        throw new BenefitsPerTierError('NOT_FOUND', 'Benefit not found.');
      }
      await requireService().cancelPendingForBenefit(target.name);
      await refresh();
    },
    [benefits, refresh, requireService],
  );

  const hasPending = useMemo(
    () => benefits.some((b) => b.pending !== undefined),
    [benefits],
  );

  const value = useMemo<BenefitsPerTierContextValue>(
    () => ({
      benefits,
      loading,
      loadError,
      hasPending,
      addBenefit,
      updateBenefit,
      removeBenefit,
      cancelPendingForBenefit,
      refresh,
    }),
    [
      benefits,
      loading,
      loadError,
      hasPending,
      addBenefit,
      updateBenefit,
      removeBenefit,
      cancelPendingForBenefit,
      refresh,
    ],
  );

  return (
    <BenefitsPerTierContext.Provider value={value}>
      {children}
    </BenefitsPerTierContext.Provider>
  );
};

export const useBenefitsPerTier = (): BenefitsPerTierContextValue => {
  const ctx = useContext(BenefitsPerTierContext);
  if (!ctx) {
    throw new Error(
      'useBenefitsPerTier must be used within a BenefitsPerTierProvider',
    );
  }
  return ctx;
};

export { BenefitsPerTierError };
